'use client';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';

import { deleteAllFromCart, type FetchCartResponse } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useDialogStore,
} from '@eliadmin/core/src/components/ui/dialog';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiTrash2 } from 'react-icons/fi';

const CLEAR_CART_DIALOG_ID = 'CLEAR_CART_DIALOG';

const CLEAR_CART_FALLBACKS = {
  sk: {
    text2025: 'Vymazať všetky',
    text4047: 'Vyprázdniť košík?',
    text4048:
      'Naozaj chcete odstrániť všetky položky z košíka? Túto akciu nie je možné vrátiť späť.',
    text377: 'Zrušiť',
    text4049: 'Áno, vyprázdniť',
    text530: 'Došlo k chybe, skúste to za chvíľu znova.',
  },
  cs: {
    text2025: 'Vymazat všechny',
    text4047: 'Vyprázdnit košík?',
    text4048: 'Opravdu chcete odstranit všechny položky z košíku? Tuto akci nelze vrátit zpět.',
    text377: 'Zrušit',
    text4049: 'Ano, vyprázdnit',
    text530: 'Došlo k chybě, zkuste to za chvíli znovu.',
  },
  en: {
    text2025: 'Clear all',
    text4047: 'Clear the cart?',
    text4048:
      'Are you sure you want to remove all items from your cart? This action cannot be undone.',
    text377: 'Cancel',
    text4049: 'Yes, clear cart',
    text530: "There's been an error, try again in a minute.",
  },
} as const;

type ClearCartTranslationKey = keyof (typeof CLEAR_CART_FALLBACKS)['sk'];

function useClearCartTranslations() {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const fallbacks = CLEAR_CART_FALLBACKS[locale === 'sk' || locale === 'cs' ? locale : 'en'];

  return (key: ClearCartTranslationKey) => {
    const translation = t(key);
    return translation === key || !translation.trim() ? fallbacks[key] : translation;
  };
}

export function ClearCartButton() {
  const t = useClearCartTranslations();
  const open = useDialogStore(state => state.open);

  return (
    <Button
      type="button"
      variant="destructive-alt"
      className="w-full gap-2 sm:w-auto"
      onClick={() =>
        open({
          id: CLEAR_CART_DIALOG_ID,
          content: <ClearCartDialog />,
        })
      }
    >
      <FiTrash2 aria-hidden />
      {t('text2025')}
    </Button>
  );
}

function ClearCartDialog() {
  const t = useClearCartTranslations();
  const { toast } = useToast();
  const close = useDialogStore(state => state.close);
  const locale = useLocaleStore(state => state.currentLocale);
  const authToken = useCustomerStore(state => state.me?.authToken);
  const queryClient = useQueryClient();
  const { queryKey: cartQueryKey } = useCartQueryOpts();

  const clearCartMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: () => deleteAllFromCart({ locale, authToken }),
    onSuccess: async response => {
      if (response?.data) {
        queryClient.setQueryData<FetchCartResponse['data']>(
          cartQueryKey,
          response.data,
        );
      }

      close(CLEAR_CART_DIALOG_ID);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: cartQueryKey }),
        queryClient.invalidateQueries({ queryKey: [CacheKey.SHIPMENTS] }),
        queryClient.invalidateQueries({ queryKey: [CacheKey.CART_PAYMENTS] }),
      ]);
    },
    onError: () => {
      toast({
        title: t('text530'),
        variant: 'destructive',
      });
    },
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('text4047')}</DialogTitle>
        <DialogDescription>{t('text4048')}</DialogDescription>
      </DialogHeader>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          type="button"
          variant="outline"
          disabled={clearCartMutation.isPending}
          onClick={() => close(CLEAR_CART_DIALOG_ID)}
        >
          {t('text377')}
        </Button>
        <Button
          type="button"
          variant="destructive"
          isLoading={clearCartMutation.isPending}
          onClick={() => clearCartMutation.mutate()}
        >
          <FiTrash2 aria-hidden />
          {t('text4049')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
